import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUserTracking } from '../services/UserActivityService';
import './SystemInfoPopup.css';

interface SystemInfoPopupProps {
    isOpen: boolean;
    onClose: () => void;
    versionInfo: {
        version: string;
        buildTime: string;
    };
}

const SystemInfoPopup: React.FC<SystemInfoPopupProps> = ({ 
    isOpen, 
    onClose, 
    versionInfo 
}) => {
    const { t } = useTranslation();
    const { deviceInfo } = useUserTracking();

    if (!isOpen) return null;

    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleString();
        } catch (error) {
            return dateString;
        }
    };

    const systemDetails = [
        {
            icon: 'info',
            label: t('systemInfo.version'),
            value: versionInfo.version
        },
        {
            icon: 'schedule',
            label: t('systemInfo.deployment'),
            value: formatDate(versionInfo.buildTime)
        },
        {
            icon: 'devices',
            label: t('systemInfo.device'),
            value: `${deviceInfo?.deviceType || t('common.unknown')} (${deviceInfo?.os || t('common.unknown')} ${deviceInfo?.osVersion || ''})`
        },
        {
            icon: 'web',
            label: t('systemInfo.browser'),
            value: `${deviceInfo?.browser || t('common.unknown')} ${deviceInfo?.browserVersion || ''}`
        },
        {
            icon: 'monitor',
            label: t('systemInfo.screen'),
            value: deviceInfo?.screenResolution || t('common.unknown')
        },
        {
            icon: 'language',
            label: t('systemInfo.userAgent'),
            value: navigator.userAgent
        }
    ];

    return (
        <div className="system-info-overlay" onClick={onClose}>
            <div className="system-info-popup" onClick={(e) => e.stopPropagation()}>
                <div className="system-info-header">
                    <h2>
                        <span className="material-icons">info</span>
                        {t('systemInfo.title')}
                    </h2>
                    <button 
                        type="button"
                        className="close-button" 
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onClose();
                        }}
                        title={t('buttons.close')}
                    >
                        <span className="material-icons">close</span>
                    </button>
                </div>
                
                <div className="system-info-content">
                    <div className="system-info-grid">
                        {systemDetails.map((detail, index) => (
                            <div key={index} className="system-info-item">
                                <div className="system-info-label">
                                    <span className="material-icons">{detail.icon}</span>
                                    <span>{detail.label}</span>
                                </div>
                                <div className="system-info-value" title={detail.value}>
                                    {detail.value}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="system-info-footer">
                        <div className="build-info">
                            <p className="app-name">LuLarge</p>
                            <p className="build-details">
                                {t('systemInfo.builtWith')} React + TypeScript + Vite
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystemInfoPopup; 